import { useState } from "react";
import { ChevronDown } from "@/shared/icons";
import { cn } from "@/shared/utils/cn";
import { useFYStore } from "../store";

export const FY_ALL = "ALL";

function fyOptions(serverCurrent: string): string[] {
  const startYearStr = serverCurrent.split("-")[0];
  const start = startYearStr ? Number(startYearStr) : new Date().getFullYear();
  const out: string[] = [FY_ALL];
  for (let y = start; y >= start - 4; y--) {
    out.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }
  return out;
}

const labelFor = (fy: string) => (fy === FY_ALL ? "All FY Years" : `FY ${fy}`);
const shortLabelFor = (fy: string) => (fy === FY_ALL ? "All FY" : fy);

export function FYSwitcher() {
  const selected = useFYStore((s) => s.selected);
  const serverCurrent = useFYStore((s) => s.serverCurrent);
  const setSelected = useFYStore((s) => s.setSelected);
  const [open, setOpen] = useState(false);
  const options = fyOptions(serverCurrent);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-md border border-[var(--line-strong)] bg-[var(--surface)] px-2.5 text-[13px] text-[var(--ink-1)] transition-colors duration-micro ease-brand hover:border-[var(--ink-4)]",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="hidden sm:inline">{labelFor(selected)}</span>
        <span className="sm:hidden">{shortLabelFor(selected)}</span>
        <ChevronDown size={14} aria-hidden />
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute right-0 top-9 z-[var(--z-popover)] min-w-[200px] overflow-hidden rounded-md border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-md)]"
        >
          {options.map((fy) => {
            const isCurrent = fy === serverCurrent;
            const isSelected = fy === selected;
            return (
              <li key={fy} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-[var(--bg-subtle)]",
                    isSelected && "bg-[var(--accent-50)] font-medium text-[var(--accent-800)]",
                  )}
                  onClick={() => {
                    setSelected(fy);
                    setOpen(false);
                  }}
                >
                  <span>{labelFor(fy)}</span>
                  {isCurrent ? (
                    <span className="text-[11px] text-[var(--ink-3)]">current</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
